import React, { useRef, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { MapPin, Loader2 } from "lucide-react";
import { usePlacesAutocomplete, loadGoogleMapsScript } from "@/hooks/usePlacesAutocomplete";

// Re-export for backwards compatibility
export { loadGoogleMapsScript };

/**
 * Suggestion Item Component
 */
function SuggestionItem({ suggestion, onSelect, testId, index }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(suggestion)}
      className="w-full flex items-start gap-3 p-3 hover:bg-secondary/50 transition-colors text-left border-b border-border last:border-0"
      data-testid={`${testId}-suggestion-${index}`}
    >
      <MapPin className="w-5 h-5 text-muted-foreground mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm truncate">{suggestion.mainText}</p>
        {suggestion.secondaryText && (
          <p className="text-xs text-muted-foreground truncate">{suggestion.secondaryText}</p>
        )}
      </div>
    </button>
  );
}

/**
 * Suggestions Dropdown Component
 */
function SuggestionsDropdown({ suggestions, onSelect, testId, dropdownRef }) {
  if (suggestions.length === 0) return null;
  
  return (
    <div 
      ref={dropdownRef}
      className="absolute z-[100] w-full mt-1 bg-background border border-border shadow-lg max-h-60 overflow-y-auto rounded-md"
      data-testid={`${testId}-dropdown`}
    >
      {suggestions.map((suggestion, index) => (
        <SuggestionItem
          key={suggestion.placeId || index}
          suggestion={suggestion}
          onSelect={onSelect}
          testId={testId}
          index={index}
        />
      ))}
    </div>
  );
}

/**
 * Google Places Autocomplete Input
 * Shows location suggestions as user types (like Uber/Ola)
 */
export function PlacesAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder = "Search location...",
  className = "",
  disabled = false,
  testId = "places-autocomplete"
}) {
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  const {
    inputValue,
    suggestions,
    showDropdown,
    loading,
    handleInputChange,
    handleSelect,
    closeDropdown,
    openDropdown
  } = usePlacesAutocomplete(value, onChange, onSelect);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        closeDropdown();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [closeDropdown]);

  return (
    <div className="relative">
      <div className="relative">
        <Input
          ref={inputRef}
          value={inputValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={openDropdown}
          placeholder={placeholder}
          className={`rounded-none pr-8 ${className}`}
          disabled={disabled}
          data-testid={testId}
          autoComplete="off"
        />
        {loading && (
          <div className="absolute right-2 top-1/2 -translate-y-1/2">
            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
          </div>
        )}
      </div>

      {/* Suggestions Dropdown */}
      {showDropdown && (
        <SuggestionsDropdown
          suggestions={suggestions}
          onSelect={handleSelect}
          testId={testId}
          dropdownRef={dropdownRef}
        />
      )}
    </div>
  );
}
